import { parseDate } from "helpers/date";
import { centsToDollars, parsePrice } from "helpers/price";
import { Timeline } from "primereact/timeline";
import React, { useState } from "react";
import styled from "styled-components";
import { IOrderHistory } from "types/Order/Order";

import { emptyStateDescription, emptyStateTitle } from "../helpers";
import { StyledCardContainer, StyledSectionContainer } from "../styled";

const StyledMarkerContainer = styled.div`
  width: 32px;
  height: 32px;
  @media (max-width: 768px) {
    width: 28px;
    height: 28px;
    i {
      font-size: 14px;
    }
  }
`;

const StyledTimeline = styled(Timeline)`
  .p-timeline-event-opposite {
    flex: 0 0;
    width: 0px;
    padding: 0px !important;
  }
  .p-timeline-event-content {
    padding-right: 0px !important;
  }

  @media (max-width: 768px) {
    .p-timeline-event-content {
      padding-left: 5px !important;
    }
  }
`;

const StyledShowMore = styled.p`
  color: var(--color-purple);
  margin-top: 30px;
`;

function BalanceHistory({
  balanceHistory,
  patientName,
  addClearFee,
  clearValueInPercentage,
}: {
  balanceHistory: IOrderHistory[] | [] | null;
  patientName: string;
  addClearFee: boolean;
  clearValueInPercentage: number;
}) {
  const [showMore, setShowMore] = useState(false);
  const custimizedMarker = (item?: IOrderHistory) => {
    const isBalanceReported = item?.type === "BalanceReported";
    let iconClass = "pi pi-pencil";
    if (item) {
      iconClass = isBalanceReported ? "pi pi-wrench" : "pi pi-dollar";
    }
    return (
      <StyledMarkerContainer
        className="flex align-items-center justify-content-center border-2 border-circle"
        style={{
          borderColor: "#4f46e5",
          color: "#4f46e5",
        }}
      >
        <i className={iconClass} />
      </StyledMarkerContainer>
    );
  };

  const generateOrderHistoryLine = (item: IOrderHistory, isEmpty?: boolean) => {
    const providerValueChanges = item?.providerValueChanges;
    const isBalanceReported = item.type === "BalanceReported";
    const isPatientReporter = item.type === "BalanceReported" && !item.user;
    let balanceUpdatedBy: React.ReactNode = item.user?.name;
    if (isPatientReporter) {
      balanceUpdatedBy = <span data-pii="patient-name">{patientName}</span>;
    }
    const type = isBalanceReported ? "Balance Reported" : "Balance Updated";
    return (
      <StyledSectionContainer className="mt-0 mb-3 md:p-3 p-2 gap-1">
        {isEmpty && (
          <>
            <p className="gray font-medium">{emptyStateTitle}</p>
            <p className="text-sm font-normal mb-2">{emptyStateDescription}</p>
          </>
        )}
        {!isEmpty && (
          <>
            <div className="w-full flex gap-2 justify-content-between align-items-center">
              <div className="border-round-3xl bg-gray-100 px-3 py-1 text-xs font-semibold">
                {type}
              </div>
              <div className="black font-medium text-sm">
                {parseDate(item.createdAt, false, true)}
              </div>
            </div>
            <div className="flex gap-2 text-xs align-items-center mt-2 gray">
              <i className="pi pi-user" style={{ fontSize: "12px" }} />
              {balanceUpdatedBy}
            </div>
            {isBalanceReported && (
              <>
                <div className="flex justify-content-between align-items-center mt-2">
                  <div className="flex gap-2 text-sm gray align-items-center">
                    <i
                      className="pi pi-building"
                      style={{ fontSize: "13px" }}
                    />
                    <span className="black font-medium">
                      {item.toProvider?.name}
                    </span>
                  </div>
                  <p className="text-sm light-green">
                    {parsePrice(centsToDollars(item.amountInCents || 0))}
                  </p>
                </div>
                <div className="flex p-2 bg-gray-50 mt-2 border-round-lg text-xs text-gray-700">
                  Notes: {item.description}
                </div>
              </>
            )}
            {!isBalanceReported &&
              providerValueChanges?.map((change, index) => (
                <div
                  className="flex justify-content-between align-items-center mt-2 gap-2"
                  key={`${change.providerId}_${index}`}
                >
                  <div className="flex gap-2 text-sm gray align-items-center">
                    <i
                      className="pi pi-building"
                      style={{ fontSize: "13px" }}
                    />
                    <span className="black font-medium">
                      {change?.provider?.name}
                    </span>
                  </div>
                  <div className="purple gap-1 flex align-items-center text-sm md:flex-row flex-column">
                    <span className="gray line-through">
                      {parsePrice(
                        centsToDollars(
                          addClearFee
                            ? change.initialValue *
                                (1 + clearValueInPercentage / 100)
                            : change.initialValue
                        )
                      )}{" "}
                    </span>
                    <i
                      className="pi pi-arrow-down md:-rotate-90 gray"
                      style={{ fontSize: "12px" }}
                    />
                    <span className="font-medium light-green">
                      {parsePrice(
                        centsToDollars(
                          addClearFee
                            ? change.newValue *
                                (1 + clearValueInPercentage / 100)
                            : change.newValue
                        )
                      )}
                    </span>
                  </div>
                </div>
              ))}
            {!isBalanceReported && item.description && (
              <div className="flex p-2 bg-gray-50 mt-2 border-round-lg text-xs text-gray-700">
                {item.description}
              </div>
            )}
          </>
        )}
      </StyledSectionContainer>
    );
  };

  return (
    <StyledCardContainer className="gap-3 mt-3 md:px-3 px-2">
      <p className="text-lg font-semibold black mb-2 flex align-items-center mb-3">
        <i
          className="pi pi-clock mr-2"
          style={{ fontSize: "20px", color: "#4f46e5" }}
        />
        Balance history
      </p>
      {!balanceHistory?.length ? (
        <StyledTimeline
          value={[{ type: "BalanceUpdated" }]}
          content={(item) => generateOrderHistoryLine(item, true)}
          marker={() => custimizedMarker()}
        />
      ) : (
        <>
          <StyledTimeline
            value={showMore ? balanceHistory : balanceHistory.slice(0, 3)}
            content={(item) => generateOrderHistoryLine(item)}
            marker={(item) => custimizedMarker(item)}
          />
          {balanceHistory?.length > 3 && (
            <StyledShowMore
              className="small-text purple flex align-items-center cursor-pointer mb-4"
              onClick={() => setShowMore(!showMore)}
            >
              {showMore ? (
                <>
                  Show Less <i className="pi pi-angle-up ml-1" />
                </>
              ) : (
                <>
                  Show More <i className="pi pi-angle-down ml-1" />
                </>
              )}
            </StyledShowMore>
          )}
        </>
      )}
    </StyledCardContainer>
  );
}

export default BalanceHistory;
